import React, { createContext, useContext, useState, useCallback, useLayoutEffect, useRef } from 'react';
import { isGameplayKeyboardField } from './inputFocus';

interface FocusContextType {
  registerOverlay: (id: string, options?: { blocksKeyboard?: boolean }) => void;
  unregisterOverlay: (id: string) => void;
  hasActiveOverlay: boolean;
  activeOverlays: Set<string>;
  setOverlaysSuspended: (suspended: boolean) => void;
}

const FocusContext = createContext<FocusContextType | null>(null);

/** Release Unity keyboard + cursor capture so the browser handles shortcuts (e.g. copy in chat input). */
export function releaseUnityCapture(): void {
  if (!window.sendMessageToUnity) return;
  try {
    const captureKeyboardData = {
      eventType: 'CaptureKeyboard',
      payload: JSON.stringify({ capture: false }),
    };
    window.sendMessageToUnity('BrowserBridge', 'ReceivePayload', JSON.stringify(captureKeyboardData));

    const captureCursorData = {
      eventType: 'CaptureCursor',
      payload: JSON.stringify({ capture: false }),
    };
    window.sendMessageToUnity('BrowserBridge', 'ReceivePayload', JSON.stringify(captureCursorData));
  } catch (error) {
    console.error('[FocusContext] Error releasing Unity capture:', error);
  }
}

/** Ordinary widgets need a free cursor, not exclusive ownership of movement keys. */
export function releaseUnityCursorCapture(): void {
  if (!window.sendMessageToUnity) return;
  try {
    window.sendMessageToUnity('BrowserBridge', 'ReceivePayload', JSON.stringify({
      eventType: 'CaptureCursor', payload: JSON.stringify({ capture: false }),
    }));
  } catch (error) {
    console.error('[FocusContext] Error releasing Unity cursor:', error);
  }
}

function enableUnityKeyboardCapture(): void {
  if (!window.sendMessageToUnity) return;
  try {
    const captureKeyboardData = {
      eventType: 'CaptureKeyboard',
      payload: JSON.stringify({ capture: true }),
    };
    window.sendMessageToUnity('BrowserBridge', 'ReceivePayload', JSON.stringify(captureKeyboardData));
  } catch (error) {
    console.error('[FocusContext] Error enabling Unity capture:', error);
  }
}

export const FocusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activeOverlays, setActiveOverlays] = useState<Set<string>>(new Set());
  const overlaysRef = useRef<Set<string>>(new Set());
  const blockingRef = useRef<Set<string>>(new Set());
  const [blockingCount, setBlockingCount] = useState(0);
  const [suspended, setSuspended] = useState(false);
  const suspendedRef = useRef(false);
  const syncCapture = useCallback(() => {
    if (!suspendedRef.current && blockingRef.current.size > 0) releaseUnityCapture();
    else enableUnityKeyboardCapture();
    if (!suspendedRef.current && overlaysRef.current.size > 0) releaseUnityCursorCapture();
  }, []);

  const setOverlaysSuspended = useCallback((value: boolean) => {
    suspendedRef.current = value;
    setSuspended(value);
    syncCapture();
  }, [syncCapture]);

  const registerOverlay = useCallback((id: string, options?: { blocksKeyboard?: boolean }) => {
    if (overlaysRef.current.has(id)) return;
    overlaysRef.current.add(id);
    // Direct registrations retain exclusive input semantics (e.g. chat resize).
    // useOverlay opts ordinary widgets out below.
    if (options?.blocksKeyboard !== false) blockingRef.current.add(id);
    setBlockingCount(blockingRef.current.size);
    setActiveOverlays(new Set(overlaysRef.current));

    syncCapture();
  }, [syncCapture]);

  const unregisterOverlay = useCallback((id: string) => {
    // Only proceed if the overlay was actually registered
    if (!overlaysRef.current.has(id)) {
      return;
    }
    
    overlaysRef.current.delete(id);
    blockingRef.current.delete(id);
    setBlockingCount(blockingRef.current.size);
    setActiveOverlays(new Set(overlaysRef.current));
    
    syncCapture();
  }, [syncCapture]);

  useLayoutEffect(() => {
    const id = 'intersect-focused-input';
    let disposed = false;
    const syncFocusedInput = () => {
      if (disposed) return;
      if (isGameplayKeyboardField(document.activeElement)) registerOverlay(id);
      else unregisterOverlay(id);
    };
    const onFocusOut = () => { Promise.resolve().then(syncFocusedInput); };
    const onReady = () => { syncFocusedInput(); syncCapture(); };
    document.addEventListener('focusin', syncFocusedInput);
    document.addEventListener('focusout', onFocusOut);
    window.addEventListener('ClientLoadedWorldAndReady', onReady);
    const observer = new MutationObserver(syncFocusedInput);
    observer.observe(document.body, { subtree: true, childList: true, attributes: true,
      attributeFilter: ['disabled', 'readonly', 'contenteditable', 'type', 'hidden', 'inert', 'aria-hidden'] });
    onReady();
    return () => {
      disposed = true;
      document.removeEventListener('focusin', syncFocusedInput);
      document.removeEventListener('focusout', onFocusOut);
      window.removeEventListener('ClientLoadedWorldAndReady', onReady);
      observer.disconnect(); unregisterOverlay(id);
    };
  }, [registerOverlay, unregisterOverlay, syncCapture]);

  // Used by canvas recapture and world-screen click handling: only input blockers count.
  const hasActiveOverlay = !suspended && blockingCount > 0;

  return (
    <FocusContext.Provider value={{ registerOverlay, unregisterOverlay, hasActiveOverlay, activeOverlays, setOverlaysSuspended }}>
      {children}
    </FocusContext.Provider>
  );
};

// Hook to use the focus context
export const useFocus = (): FocusContextType => {
  const context = useContext(FocusContext);
  if (!context) {
    throw new Error('useFocus must be used within a FocusProvider');
  }
  return context;
};

// Convenience hook for overlays - automatically registers/unregisters based on isOpen state
export const useOverlay = (id: string, isOpen: boolean, options?: { modal?: boolean }): void => {
  const { registerOverlay, unregisterOverlay } = useFocus();
  const modal = options?.modal === true;

  useLayoutEffect(() => {
    if (isOpen) {
      registerOverlay(id, { blocksKeyboard: modal });
      // Only unregister via cleanup when overlay was actually registered
      return () => {
        unregisterOverlay(id);
      };
    }
    // No cleanup needed if overlay wasn't registered
  }, [id, isOpen, modal, registerOverlay, unregisterOverlay]);
};

export default FocusContext;
